import { Link } from "react-router-dom";
import { useTeamSchedule } from "./TeamScheduleContext.js";
import { isHexColorLightOrDark } from "./isHexColorLightOrDark.js";
import { cn, useIsGummy } from "@swivu/shared-ui";

interface TaskItemProps {
  taskId?: number;
  taskDisplayName?: string;
  link?: string;
  color?: string;
  startsAt?: string;
  endsAt?: string;
  viewTransitionUnique?: string;
  style?: string | undefined | null;
  viewTransitions?: {
    background?: string;
    title?: string;
  };
  row?: string;
  gridColumn?: string;
}

const TaskItem = ({
  startsAt,
  endsAt,
  link,
  taskDisplayName,
  color,
  viewTransitions,
  viewTransitionUnique,
  style,
  row,
  gridColumn
}: TaskItemProps) => {
  const { infoDensity } = useTeamSchedule();
  const isGummy = useIsGummy();
  const [href, search] = (link ?? "").split("?");
  const params = new URLSearchParams(search);
  if (viewTransitionUnique) {
    params.set("uniqueViewTransition", viewTransitionUnique);
  }
  const textColor = isHexColorLightOrDark(color ?? "#000");
  const gummyBackgroundImage =
    style === "rounded-sm"
      ? "var(--task-overlay-frosted)"
      : "var(--task-overlay)";
  return (
    <Link
      // preventScrollReset={true}
      to={`${href}${params.toString() ? `?${params.toString()}` : ""}`}
      viewTransition={true}
      style={
        isGummy
          ? {
              backgroundImage: gummyBackgroundImage,
              gridColumn: `${gridColumn} / span 1`,
              gridRow: `${row} / span 1`,
              backgroundColor: color ?? "transparent",
              color: textColor,
              textShadow:
                textColor === "white" ? "var(--text-shadow-on-color)" : "none",
              boxShadow: "var(--task-shadow)"
            }
          : {
              backgroundImage:
                style === "rounded-sm"
                  ? "linear-gradient(rgba(255, 255, 255, 0.48), rgba(255, 255, 255, 0.45))"
                  : undefined,
              gridColumn: `${gridColumn} / span 1`,
              gridRow: `${row} / span 1`,
              backgroundColor: color ?? "transparent",
              color: textColor
            }
      }
      className={cn(
        "grow flex flex-col relative overflow-hidden h-full",
        isGummy
          ? cn(
              "rounded-surface-md px-4 py-3 text-base",
              "transition-transform duration-150 gummy-lift active:scale-[0.96] motion-reduce:transform-none",
              "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/80"
            )
          : "py-0.5 px-2 rounded-md py-1 text-sm"
      )}
    >
      <div
        className={cn(
          infoDensity.value,
          "relative leading-tight",
          isGummy && "font-display font-black tracking-tight"
        )}
        style={{
          ...(viewTransitions?.title && {
            viewTransitionName: `${viewTransitions.title}${
              viewTransitionUnique ? `-${viewTransitionUnique}` : ``
            }`
          })
        }}
      >
        {taskDisplayName}
      </div>
      {(startsAt || endsAt) && (
        <div
          style={isGummy ? undefined : { borderTopColor: textColor }}
          className={cn(
            "flex justify-between pt-1",
            isGummy
              ? "text-xs font-display font-black opacity-90"
              : "[font-size:8px] md:text-xs opacity-50"
          )}
        >
          <span className="self-start">{startsAt}</span>
          <span className="self-end">{endsAt}</span>
        </div>
      )}
    </Link>
  );
};

export default TaskItem;
